import type { AiJob, AiJobStatus } from './supabase.types';

export function aiJobKind(status: AiJobStatus): 'spec' | 'build' {
  if (status === 'cancelled' || status.startsWith('spec')) return 'spec';
  return 'build';
}

export function aiJobChipLabel(job: AiJob): string {
  if (job.kind === 'content') {
    const phase = job.status.startsWith('unwind')
      ? 'Unwind'
      : aiJobKind(job.status) === 'spec'
        ? 'Outline'
        : 'Article';
    return `${phase} · ${job.title}`;
  }
  if (job.kind === 'sweep') return `Sweep · ${job.title}`;
  if (job.kind === 'offers') return `Offers · ${job.title}`;
  const kind = job.status.startsWith('unwind') ? 'Unwind' : aiJobKind(job.status) === 'spec' ? 'Spec' : 'Build';
  return `${kind} · ${job.title}`;
}

export function isAiJobInProgress(status: AiJobStatus): boolean {
  return (
    status === 'spec_in_progress' ||
    status === 'build_in_progress' ||
    status === 'unwind_in_progress' ||
    status === 'approval_in_progress'
  );
}

export const AI_JOB_STATUS_LABEL: Record<AiJobStatus, string> = {
  spec_queued: 'Spec queued',
  spec_in_progress: 'Spec in progress',
  spec_ready: 'Spec ready',
  spec_revision: 'Spec revision',
  build_queued: 'Build queued',
  build_in_progress: 'Build in progress',
  build_ready: 'Build ready',
  build_revision: 'Build revision',
  build_staged: 'Build staged',
  unwind_queued: 'Unwind queued',
  unwind_in_progress: 'Unwind in progress',
  unwind_ready: 'Ready to delete',
  approved: 'Approved',
  approval_in_progress: 'Approving',
  complete: 'Complete',
  submitted: 'Submitted',
  cancelled: 'Cancelled',
};

export function aiJobStatusTone(status: AiJobStatus): 'up' | 'warn' | 'accent' | 'neutral' | 'info' | 'down' {
  switch (status) {
    case 'spec_in_progress':
    case 'build_in_progress':
    case 'approval_in_progress':
      return 'info';
    case 'unwind_in_progress':
      return 'down';
    case 'spec_ready':
    case 'build_ready':
    case 'approved':
    case 'complete':
      return 'up';
    case 'unwind_ready':
      return 'down';
    case 'submitted':
      return 'up';
    case 'cancelled':
      return 'neutral';
    case 'build_staged':
      return 'info';
    case 'spec_queued':
    case 'spec_revision':
    case 'build_queued':
    case 'build_revision':
    case 'unwind_queued':
      return 'warn';
  }
}

/** Compact elapsed time from a claim timestamp, e.g. 12s, 4m, 1h 2m. */
export function formatClaimElapsed(fromIso: string | null | undefined, now = Date.now()): string {
  if (!fromIso) return '';
  const start = new Date(fromIso).getTime();
  if (!Number.isFinite(start)) return '';
  const sec = Math.max(0, Math.floor((now - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `${hr}h ${rem}m` : `${hr}h`;
}

const STALE_MS = 2 * 60 * 60 * 1000;

export function isClaimStale(claimedAt: string | null | undefined, now = Date.now()): boolean {
  if (!claimedAt) return false;
  const start = new Date(claimedAt).getTime();
  if (!Number.isFinite(start)) return false;
  return now - start >= STALE_MS;
}
